// locationService.js
import {PermissionsAndroid} from 'react-native';
import Geolocation from 'react-native-geolocation-service';
import {store} from '../store';
import {showAlert} from '../actions/AlertActions';
import FaJson from 'src/i18n/fa-IR.json';
import {isWeb, isAndroid} from '../constants';

const WEB_TIMEOUT = 12000;

let latestPosition = null;
let webLiveWatch = null; // Watch همیشه فعال روی وب

/**
 * -----------------------------------------------------
 *   بخش وب — نسخه PWA + Safari iOS سازگار و پایدار
 * -----------------------------------------------------
 */

// شروع watchPosition دائمی (اجباری برای iOS/PWA)
export const startWebLiveLocation = () => {
  if (!navigator.geolocation) return;
  if (webLiveWatch) return; // دوبار اجرا نشه

  console.log('%c[WEB] Live GPS session started', 'color: green');

  webLiveWatch = navigator.geolocation.watchPosition(
    pos => {
      const {latitude, longitude, accuracy} = pos.coords;

      // فقط مقادیر معتبر
      if (
        pos &&
        pos.coords &&
        pos.coords.latitude != null &&
        pos.coords.longitude != null
      ) {
        latestPosition = pos;
      }
    },
    err => {
      console.warn('[WEB] GPS Watch Error:', err);
    },
    {
      enableHighAccuracy: false,
      maximumAge: 10000, // استفاده از cache کوتاه
      timeout: WEB_TIMEOUT,
    },
  );
};

// برای زمانی که اپ بسته میشه یا logout
export const stopWebLiveLocation = () => {
  if (webLiveWatch) {
    navigator.geolocation.clearWatch(webLiveWatch);
    webLiveWatch = null;
  }
};

/**
 * درخواست دستی لوکیشن وب (در صورت نیاز)
 * همیشه از آخرین لوکیشن معتبر session استفاده می‌کنیم
 */
const getWebPosition = async () => {
  // اگر قبلاً یک موقعیت معتبر گرفته شده
  if (latestPosition) return latestPosition;

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    console.warn('[WEB] Geolocation API not available');
    return null;
  }

  // مرحله 1: تلاش با getCurrentPosition
  const pos = await new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      success => {
        latestPosition = success;
        resolve(success);
      },
      error => {
        console.warn(
          '[WEB] getCurrentPosition FAILED → fallback to watchPosition',
          error,
        );
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: WEB_TIMEOUT,
        maximumAge: 10000,
      },
    );
  });

  // اگر getCurrentPosition موفق بود → تمام
  if (pos) return pos;

  // مرحله 2: fallback با watchPosition (سازگار با Safari)
  return new Promise(resolve => {
    const watchId = navigator.geolocation.watchPosition(
      success => {
        latestPosition = success;
        navigator.geolocation.clearWatch(watchId);
        resolve(success); // اولین لوکیشن را سریع برگرداند
      },
      error => {
        console.warn('[WEB] watchPosition fallback FAILED', error);
        navigator.geolocation.clearWatch(watchId);
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
      },
    );

    // اگر بعد از X ثانیه هنوز لوکیشن نداد → رها کن
    setTimeout(() => {
      navigator.geolocation.clearWatch(watchId);
      resolve(null);
    }, 20000);
  });
};

/**
 * -----------------------------------------------------
 *   بخش موبایل (React Native)
 * -----------------------------------------------------
 */
const getMobilePosition = async () => {
  // Permissions Android
  if (isAndroid) {
    const granted = await PermissionsAndroid.check(
      PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
    );

    if (!granted) {
      const request = await PermissionsAndroid.request(
        PermissionsAndroid.PERMISSIONS.ACCESS_FINE_LOCATION,
      );

      if (request !== PermissionsAndroid.RESULTS.GRANTED) {
        return null;
      }
    }
  }

  return new Promise(resolve => {
    let settled = false;
    let watchId = null;

    const finish = value => {
      if (settled) {
        return;
      }
      settled = true;
      if (watchId != null) {
        Geolocation.clearWatch(watchId);
      }
      resolve(value);
    };

    watchId = Geolocation.watchPosition(
      pos => {
        const {latitude, longitude} = pos.coords;

        if (latitude !== 0 && longitude !== 0) {
          latestPosition = pos;
          finish(pos);
        }
      },
      error => {
        console.warn('[MOBILE GPS ERROR]', error);

        store.dispatch(
          showAlert({
            id: 'AlertBox',
            modalProps: {
              icon: 'redExclamation',
              title: FaJson.location_error_title,
              message: FaJson.turn_on_gps,
            },
          }),
        );

        finish(latestPosition);
      },
      {
        enableHighAccuracy: true,
        distanceFilter: 0,
        interval: 2000,
        fastestInterval: 1000,
        showLocationDialog: true,
      },
    );

    setTimeout(() => {
      finish(latestPosition);
    }, 12000);
  });
};

/**
 * -----------------------------------------------------
 *   تابع اصلی
 * -----------------------------------------------------
 */

// گرفتن لوکیشن (برای پایان سفر، شروع سفر، ...)
export const getLocation = async callback => {
  try {
    let position;

    if (isWeb) {
      // همیشه از session زنده استفاده می‌کنیم
      position = await getWebPosition();
    } else {
      position = await getMobilePosition();
    }

    callback(position ?? null);
  } catch (err) {
    console.error('[LOCATION ERROR]', err);
    callback(null);
  }
};

// همیشه آخرین پوزیشن معتبر را برمی‌گرداند
export const validateLocationBeforeReserve = () => latestPosition;
